import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ROLES } from '@bcc/shared';
import { SITE_GLASS_CARD, SITE_FORM_CONTROL } from '../constants/siteUi';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, apiPostMultipart, ApiError } from '../lib/api';
import {
  getSavedDealAnalysis,
  saveDealAnalysisRecord,
} from '../lib/savedDealAnalyses';
import { DealAnalyzerResults } from '../components/dealAnalyzer/DealAnalyzerResults';
import type { DealAnalyzerAnalysis } from '../types/dealAnalyzer';

const MAX_FILE_BYTES = 50 * 1024 * 1024;
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,image/*';

function parsePurchasePriceInput(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function parseOptionalNumber(s: string): number | undefined {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

function InfoTip({ text }: { text: string }) {
  return (
    <span className="inline-flex shrink-0" title={text}>
      <span className="flex size-4 cursor-help items-center justify-center rounded-full border border-white/20 bg-white/5 text-[10px] font-bold text-[var(--home-muted-on-dark)]">
        i
      </span>
      <span className="sr-only">{text}</span>
    </span>
  );
}

function LabelWithTip({ htmlFor, tip, children }: { htmlFor: string; tip: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="flex items-start justify-between gap-2 font-body text-sm text-[var(--home-text-on-dark)]">
      <span className="leading-snug">{children}</span>
      <InfoTip text={tip} />
    </label>
  );
}

const STEPS = [
  'Set financing assumptions — configure loan terms, down payment, and CapEx reserves.',
  'Provide property data — upload an OM (Offering Memorandum), rent roll, T12, or other property documents.',
  'Review extracted data — the AI pulls key financials. Fill in anything it missed.',
  'View results — see NOI, cap rate, cash flow, cash-on-cash return, and projected charts.',
  'Export report — download a detailed underwriting report as Excel or PDF with charts.',
] as const;

export function InvestorDealAnalyzer() {
  const formId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const { session, role } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cashPurchase, setCashPurchase] = useState(false);
  const [purchasePrice, setPurchasePrice] = useState('');
  const [downPaymentPct, setDownPaymentPct] = useState('35');
  const [interestRatePct, setInterestRatePct] = useState('6');
  const [loanTermYrs, setLoanTermYrs] = useState('5');
  const [ioPeriodMos, setIoPeriodMos] = useState('48');
  const [amortizationYrs, setAmortizationYrs] = useState('30');
  const [capexClosing, setCapexClosing] = useState('0');
  const [closingCostsPct, setClosingCostsPct] = useState('2.5');
  const [reservePerUnit, setReservePerUnit] = useState('250');
  const [holdPeriodYrs, setHoldPeriodYrs] = useState('5');
  const [occupancyPct, setOccupancyPct] = useState('');
  const [avgRentPerUnit, setAvgRentPerUnit] = useState('');
  const [otherIncome, setOtherIncome] = useState('');
  const [netRentalIncome, setNetRentalIncome] = useState('');
  const [expensesAnnual, setExpensesAnnual] = useState('');
  const [rentGrowthPct, setRentGrowthPct] = useState('');
  const [expenseGrowthPct, setExpenseGrowthPct] = useState('');
  const [exitCapRatePct, setExitCapRatePct] = useState('');

  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<DealAnalyzerAnalysis | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [analyzerReady, setAnalyzerReady] = useState<boolean | null>(null);

  const addFiles = useCallback((list: FileList | File[]) => {
    const arr = Array.from(list);
    for (const f of arr) {
      if (f.size > MAX_FILE_BYTES) {
        setErr(`"${f.name}" exceeds 50MB.`);
        return;
      }
    }
    setErr(null);
    setFiles((prev) => [...prev, ...arr]);
  }, []);

  const removeFile = (i: number) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const applyAssumptionsFromAnalysis = useCallback((a: DealAnalyzerAnalysis['assumptions']) => {
    if (!a) return;
    if (a.cashPurchase != null) setCashPurchase(a.cashPurchase);
    if (a.purchasePrice != null) setPurchasePrice(String(a.purchasePrice));
    else if (a.purchasePrice === null) setPurchasePrice('');
    if (a.downPaymentPct != null) setDownPaymentPct(String(a.downPaymentPct));
    if (a.interestRatePct != null) setInterestRatePct(String(a.interestRatePct));
    if (a.loanTermYrs != null) setLoanTermYrs(String(a.loanTermYrs));
    if (a.ioPeriodMos != null) setIoPeriodMos(String(a.ioPeriodMos));
    if (a.amortizationYrs != null) setAmortizationYrs(String(a.amortizationYrs));
    if (a.capexClosing != null) setCapexClosing(String(a.capexClosing));
    if (a.closingCostsPct != null) setClosingCostsPct(String(a.closingCostsPct));
    if (a.reservePerUnit != null) setReservePerUnit(String(a.reservePerUnit));
    if (a.holdPeriodYrs != null) setHoldPeriodYrs(String(a.holdPeriodYrs));
    if (a.occupancyPct != null) setOccupancyPct(String(a.occupancyPct));
    if (a.avgRentPerUnit != null) setAvgRentPerUnit(String(a.avgRentPerUnit));
    if (a.otherIncome != null) setOtherIncome(String(a.otherIncome));
    if (a.netRentalIncome != null) setNetRentalIncome(String(a.netRentalIncome));
    if (a.expensesAnnual != null) setExpensesAnnual(String(a.expensesAnnual));
    if (a.rentGrowthPct != null) setRentGrowthPct(String(a.rentGrowthPct));
    if (a.expenseGrowthPct != null) setExpenseGrowthPct(String(a.expenseGrowthPct));
    if (a.exitCapRatePct != null) setExitCapRatePct(String(a.exitCapRatePct));
  }, []);

  useEffect(() => {
    const loadId = searchParams.get('load');
    if (!loadId) return;
    const rec = getSavedDealAnalysis(loadId);
    if (!rec) {
      setErr('That saved analysis was not found on this device.');
      setSearchParams({}, { replace: true });
      return;
    }
    setErr(null);
    setSaveNotice(null);
    setAnalysis(rec.analysis);
    applyAssumptionsFromAnalysis(rec.analysis.assumptions);
    setSearchParams({}, { replace: true });
    window.requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [searchParams, setSearchParams, applyAssumptionsFromAnalysis]);

  useEffect(() => {
    const tok = session?.access_token;
    if (!tok) {
      setAnalyzerReady(null);
      return;
    }
    let cancelled = false;
    void apiFetch<{ ready: boolean }>('/api/deal-analyzer/status', { token: tok }).then(
      (r) => {
        if (!cancelled) setAnalyzerReady(r.ready);
      },
      () => {
        if (!cancelled) setAnalyzerReady(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [session?.access_token]);

  function onSaveAnalysis() {
    if (!analysis) return;
    setSaveNotice(null);
    try {
      const fromAddress = analysis.property?.address?.trim();
      const label =
        fromAddress ||
        `Analysis — ${new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`;
      saveDealAnalysisRecord(label, analysis);
      setSaveNotice('Saved on this device. Open Saved analyses to view or reload.');
      window.setTimeout(() => setSaveNotice(null), 5000);
    } catch (caught) {
      setErr(caught instanceof Error ? caught.message : 'Could not save analysis.');
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!session?.access_token) {
      setErr('You need to be signed in to run the deal analyzer.');
      return;
    }
    if (analyzerReady !== true) {
      setErr(
        'Deal analyzer is not ready yet. Add your API keys under Dashboard → Analyzer API keys, or ask an administrator to configure organization defaults.',
      );
      return;
    }
    if (files.length === 0) {
      setErr('Add at least one property document (PDF, Word, Excel, or image).');
      return;
    }

    const payload = {
      cashPurchase,
      purchasePrice: parsePurchasePriceInput(purchasePrice),
      downPaymentPct: parseOptionalNumber(downPaymentPct),
      interestRatePct: parseOptionalNumber(interestRatePct),
      loanTermYrs: parseOptionalNumber(loanTermYrs),
      ioPeriodMos: parseOptionalNumber(ioPeriodMos),
      amortizationYrs: parseOptionalNumber(amortizationYrs),
      capexClosing: parseOptionalNumber(capexClosing),
      closingCostsPct: parseOptionalNumber(closingCostsPct),
      reservePerUnit: parseOptionalNumber(reservePerUnit),
      holdPeriodYrs: parseOptionalNumber(holdPeriodYrs),
      occupancyPct: parseOptionalNumber(occupancyPct),
      avgRentPerUnit: parseOptionalNumber(avgRentPerUnit),
      otherIncome: parseOptionalNumber(otherIncome),
      netRentalIncome: parseOptionalNumber(netRentalIncome),
      expensesAnnual: parseOptionalNumber(expensesAnnual),
      rentGrowthPct: parseOptionalNumber(rentGrowthPct),
      expenseGrowthPct: parseOptionalNumber(expenseGrowthPct),
      exitCapRatePct: parseOptionalNumber(exitCapRatePct),
    };

    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    fd.append('payload', JSON.stringify(payload));

    setAnalyzing(true);
    try {
      const { analysis: next } = await apiPostMultipart<{ analysis: DealAnalyzerAnalysis }>(
        '/api/deal-analyzer/analyze',
        fd,
        { token: session.access_token }
      );
      setAnalysis(next);
      applyAssumptionsFromAnalysis(next.assumptions);
      window.requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setErr(caught.message);
      } else if (caught instanceof Error) {
        setErr(caught.message);
      } else {
        setErr('Analysis failed. Try again or use a smaller document.');
      }
    } finally {
      setAnalyzing(false);
    }
  }

  const inputGrid = 'grid gap-4 sm:grid-cols-2 xl:grid-cols-4';
  const fieldWrap = 'space-y-1.5';

  return (
    <div className="space-y-8">
      {analyzerReady === false ? (
        <div
          role="status"
          className="rounded-xl border border-amber-400/35 bg-amber-950/45 px-4 py-3 text-sm text-amber-100 shadow-lg shadow-black/20 ring-1 ring-amber-400/15"
        >
          <p className="font-semibold text-amber-200">Deal analyzer needs API credentials.</p>
          <p className="mt-2 text-amber-100/95">
            <Link
              to="/dashboard/ai-api-settings"
              className="font-medium text-[var(--home-miami-blue)] underline-offset-2 hover:underline"
            >
              Open Analyzer API keys
            </Link>{' '}
            to add your own OpenAI or Anthropic keys (recommended).
            {role === ROLES.ADMIN ? (
              <>
                {' '}
                Admins can also set{' '}
                <Link
                  to="/admin/ai-api-settings"
                  className="font-medium text-[var(--home-miami-blue)] underline-offset-2 hover:underline"
                >
                  organization-wide defaults
                </Link>{' '}
                for investors without personal keys.
              </>
            ) : (
              <>
                {' '}
                If your organization provides defaults, an administrator can configure those under Admin → AI API Settings.
              </>
            )}
          </p>
        </div>
      ) : null}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--home-miami-blue)]">
            Tools
          </p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--home-text-on-dark)] md:text-3xl">
            Comprehensive Deal Analyzer
          </h1>
          <p className="max-w-3xl font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
            Extract property data from uploaded documents and automatically analyze investment potential.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Link
            to="/dashboard/tools"
            className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2.5 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)] transition hover:bg-white/10"
          >
            Tools
          </Link>
          <Link
            to="/dashboard/saved-analyses"
            className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2.5 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)] transition hover:bg-white/10"
          >
            Saved analyses
          </Link>
          <button
            type="button"
            onClick={onSaveAnalysis}
            disabled={!analysis}
            className="rounded-full border border-[var(--home-miami-blue)]/40 bg-[var(--home-miami-blue)]/20 px-4 py-2.5 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-miami-blue)] transition hover:bg-[var(--home-miami-blue)]/30 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Save analysis
          </button>
        </div>
      </header>

      {saveNotice ? (
        <p
          className="rounded-xl border border-emerald-500/30 bg-emerald-950/35 px-4 py-3 font-body text-sm text-emerald-100"
          role="status"
        >
          {saveNotice}
        </p>
      ) : null}

      <section className={`${SITE_GLASS_CARD} p-5 md:p-6`}>
        <h2 className="font-display text-sm font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)]">
          How to use this calculator
        </h2>
        <ol className="mt-4 list-decimal space-y-2 pl-5 font-body text-sm leading-relaxed text-[var(--home-text-on-dark)]/90">
          {STEPS.map((s, i) => (
            <li key={i} className="marker:font-semibold marker:text-[var(--home-miami-blue)]">
              {s}
            </li>
          ))}
        </ol>
        <div className="mt-4 rounded-xl border border-[var(--home-miami-blue)]/25 bg-[var(--home-miami-blue)]/10 px-4 py-3 font-body text-xs leading-relaxed text-[var(--home-text-on-dark)]/90">
          <span className="font-semibold text-[var(--home-miami-blue)]">Tip:</span> Run <strong>Analyze deal</strong> to send
          documents to the model; returned assumptions fill the form and the full dashboard appears below.
        </div>
      </section>

      <form id={formId} onSubmit={onSubmit} className="space-y-8">
        {err ? (
          <p className="rounded-xl border border-rose-500/35 bg-rose-950/40 px-4 py-3 font-body text-sm text-rose-100" role="alert">
            {err}
          </p>
        ) : null}

        {/* Financing assumptions */}
        <div className={`${SITE_GLASS_CARD} overflow-hidden p-0`}>
          <div className="border-b border-white/[0.08] bg-[var(--home-miami-blue)]/[0.18] px-4 py-3 md:px-6">
            <h2 className="font-display text-xs font-bold uppercase tracking-[0.14em] text-[var(--home-text-on-dark)]">
              Financing assumptions
            </h2>
            <p className="mt-1 font-body text-xs text-[var(--home-muted-on-dark)]">
              Fields can be left blank if the data appears in uploaded documents.
            </p>
          </div>
          <div className="space-y-6 p-4 md:p-6">
            <div className="space-y-4">
              <p className="font-display text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                Financing
              </p>
              <label className="flex cursor-pointer items-center gap-3 font-body text-sm text-[var(--home-text-on-dark)]">
                <input
                  type="checkbox"
                  checked={cashPurchase}
                  onChange={(e) => setCashPurchase(e.target.checked)}
                  className="size-4 rounded border-white/20 bg-white/10 text-[var(--home-miami-blue)] focus:ring-[var(--home-miami-blue)]"
                />
                Cash purchase
              </label>

              <div className={inputGrid}>
                <div className={fieldWrap}>
                  <LabelWithTip
                    htmlFor={`${formId}-price`}
                    tip="Total acquisition basis before typical closing adjustments."
                  >
                    Purchase price ($)
                  </LabelWithTip>
                  <input
                    id={`${formId}-price`}
                    type="number"
                    min={0}
                    step={1}
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                    placeholder="Optional"
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-dp`} tip="Equity as a percentage of purchase price.">
                    Down payment (%)
                  </LabelWithTip>
                  <input
                    id={`${formId}-dp`}
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={downPaymentPct}
                    onChange={(e) => setDownPaymentPct(e.target.value)}
                    disabled={cashPurchase}
                    className={`w-full ${SITE_FORM_CONTROL} disabled:opacity-45`}
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-rate`} tip="Annual interest rate on the senior loan.">
                    Interest rate (%)
                  </LabelWithTip>
                  <input
                    id={`${formId}-rate`}
                    type="number"
                    min={0}
                    step={0.01}
                    value={interestRatePct}
                    onChange={(e) => setInterestRatePct(e.target.value)}
                    disabled={cashPurchase}
                    className={`w-full ${SITE_FORM_CONTROL} disabled:opacity-45`}
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-term`} tip="Loan maturity in years.">
                    Loan term (yrs)
                  </LabelWithTip>
                  <input
                    id={`${formId}-term`}
                    type="number"
                    min={1}
                    step={1}
                    value={loanTermYrs}
                    onChange={(e) => setLoanTermYrs(e.target.value)}
                    disabled={cashPurchase}
                    className={`w-full ${SITE_FORM_CONTROL} disabled:opacity-45`}
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-io`} tip="Months of interest-only payments before amortization begins.">
                    IO period (mos)
                  </LabelWithTip>
                  <input
                    id={`${formId}-io`}
                    type="number"
                    min={0}
                    step={1}
                    value={ioPeriodMos}
                    onChange={(e) => setIoPeriodMos(e.target.value)}
                    disabled={cashPurchase}
                    className={`w-full ${SITE_FORM_CONTROL} disabled:opacity-45`}
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-amort`} tip="Amortization period after IO, in years.">
                    Amortization (yrs)
                  </LabelWithTip>
                  <input
                    id={`${formId}-amort`}
                    type="number"
                    min={1}
                    step={1}
                    value={amortizationYrs}
                    onChange={(e) => setAmortizationYrs(e.target.value)}
                    disabled={cashPurchase}
                    className={`w-full ${SITE_FORM_CONTROL} disabled:opacity-45`}
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-capex`} tip="Capital repairs or capex funded at closing.">
                    CapEx at closing ($)
                  </LabelWithTip>
                  <input
                    id={`${formId}-capex`}
                    type="number"
                    min={0}
                    step={1}
                    value={capexClosing}
                    onChange={(e) => setCapexClosing(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-cc`} tip="Estimated closing costs as a percentage of purchase price.">
                    Closing costs (%)
                  </LabelWithTip>
                  <input
                    id={`${formId}-cc`}
                    type="number"
                    min={0}
                    step={0.1}
                    value={closingCostsPct}
                    onChange={(e) => setClosingCostsPct(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-white/[0.06] pt-6">
              <p className="font-display text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                Property &amp; income
              </p>
              <div className={`${inputGrid} mt-4`}>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-res`} tip="Annual replacement or capital reserve per unit.">
                    Reserve/unit ($/yr)
                  </LabelWithTip>
                  <input
                    id={`${formId}-res`}
                    type="number"
                    min={0}
                    step={1}
                    value={reservePerUnit}
                    onChange={(e) => setReservePerUnit(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-hold`} tip="Intended investment holding period.">
                    Hold period (yrs)
                  </LabelWithTip>
                  <input
                    id={`${formId}-hold`}
                    type="number"
                    min={1}
                    step={1}
                    value={holdPeriodYrs}
                    onChange={(e) => setHoldPeriodYrs(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-occ`} tip="Economic occupancy or leased % (0–100).">
                    Occupancy (%)
                  </LabelWithTip>
                  <input
                    id={`${formId}-occ`}
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={occupancyPct}
                    onChange={(e) => setOccupancyPct(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                    placeholder="Optional"
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-arpu`} tip="Average in-place rent per unit per month.">
                    Avg rent/unit ($/mo)
                  </LabelWithTip>
                  <input
                    id={`${formId}-arpu`}
                    type="number"
                    min={0}
                    step={1}
                    value={avgRentPerUnit}
                    onChange={(e) => setAvgRentPerUnit(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                    placeholder="Optional"
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-othinc`} tip="Laundry, pet fees, parking, and other annual income.">
                    Other income ($/yr)
                  </LabelWithTip>
                  <input
                    id={`${formId}-othinc`}
                    type="number"
                    min={0}
                    step={1}
                    value={otherIncome}
                    onChange={(e) => setOtherIncome(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                    placeholder="Optional"
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-nri`} tip="Net rental income from T12 or pro forma (annual).">
                    Net rental income ($/yr)
                  </LabelWithTip>
                  <input
                    id={`${formId}-nri`}
                    type="number"
                    min={0}
                    step={1}
                    value={netRentalIncome}
                    onChange={(e) => setNetRentalIncome(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                    placeholder="Optional"
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-opex`} tip="Total operating expenses (annual), excluding debt service.">
                    Operating expenses ($/yr)
                  </LabelWithTip>
                  <input
                    id={`${formId}-opex`}
                    type="number"
                    min={0}
                    step={1}
                    value={expensesAnnual}
                    onChange={(e) => setExpensesAnnual(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                    placeholder="Optional"
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-rg`} tip="Annual rent growth assumption for projections.">
                    Rent growth (%/yr)
                  </LabelWithTip>
                  <input
                    id={`${formId}-rg`}
                    type="number"
                    min={0}
                    step={0.1}
                    value={rentGrowthPct}
                    onChange={(e) => setRentGrowthPct(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                    placeholder="Optional"
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-eg`} tip="Annual expense growth assumption.">
                    Expense growth (%/yr)
                  </LabelWithTip>
                  <input
                    id={`${formId}-eg`}
                    type="number"
                    min={0}
                    step={0.1}
                    value={expenseGrowthPct}
                    onChange={(e) => setExpenseGrowthPct(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                    placeholder="Optional"
                  />
                </div>
                <div className={fieldWrap}>
                  <LabelWithTip htmlFor={`${formId}-exit`} tip="Exit capitalization rate for disposition and sensitivity.">
                    Exit cap rate (%)
                  </LabelWithTip>
                  <input
                    id={`${formId}-exit`}
                    type="number"
                    min={0}
                    step={0.01}
                    value={exitCapRatePct}
                    onChange={(e) => setExitCapRatePct(e.target.value)}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Property data upload */}
        <div className={`${SITE_GLASS_CARD} overflow-hidden p-0`}>
          <div className="border-b border-white/[0.08] bg-[var(--home-miami-blue)]/[0.18] px-4 py-3 md:px-6">
            <h2 className="font-display text-xs font-bold uppercase tracking-[0.14em] text-[var(--home-text-on-dark)]">
              Property data
            </h2>
            <p className="mt-1 font-body text-xs text-[var(--home-muted-on-dark)]">
              Upload documents containing property financials for AI-powered extraction.
            </p>
          </div>
          <div className="p-4 md:p-6">
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className={`flex min-h-[180px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 py-10 text-center transition ${
                dragOver
                  ? 'border-[var(--home-miami-blue)] bg-[var(--home-miami-blue)]/10'
                  : 'border-white/15 bg-[var(--home-zone-dark)]/30 hover:border-white/25'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <svg className="mb-3 size-11 text-[var(--home-miami-blue)]" fill="none" viewBox="0 0 24 24" aria-hidden>
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.08-2.904 5.25 5.25 0 011.857.387m1.498-2.218a5.25 5.25 0 00-8.72 4.107 4.5 4.5 0 00-1.029 8.845M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <p className="font-body text-sm font-medium text-[var(--home-text-on-dark)]">
                Drag and drop files here, or click to browse
              </p>
              <p className="mt-2 font-body text-xs text-[var(--home-muted-on-dark)]">
                PDF, Word, Excel, or images (max 50MB each)
              </p>
            </div>

            {files.length > 0 ? (
              <ul className="mt-4 space-y-2 font-body text-xs text-[var(--home-text-on-dark)]">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2"
                  >
                    <span className="min-w-0 truncate">{f.name}</span>
                    <span className="shrink-0 text-[var(--home-muted-on-dark)]">
                      {(f.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="shrink-0 rounded-lg px-2 py-1 text-rose-300 hover:bg-rose-500/15"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="flex justify-center pt-2">
          <button
            type="submit"
            disabled={analyzing || analyzerReady !== true}
            title={analyzerReady === false ? 'Configure AI API keys in Admin → AI API Settings first' : undefined}
            className="rounded-full bg-[var(--home-miami-blue)] px-10 py-3.5 font-display text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-black/25 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
          >
            {analyzing ? 'Analyzing…' : analyzerReady === false ? 'Analyze deal (locked)' : 'Analyze deal'}
          </button>
        </div>
      </form>

      {analysis ? (
        <div ref={resultsRef} className="scroll-mt-8">
          <DealAnalyzerResults analysis={analysis} />
        </div>
      ) : null}
    </div>
  );
}
